import { Vote } from 'lucide-react'
import {
	ELECTION_DATE,
	ELECTION_LABEL,
	MANDATE_START_DATE,
} from '#app/utils/election.ts'
import { useNow } from '#app/utils/use-now.ts'

interface HomeCountdownProps {
	serverNow: number
}

const formattedElectionDate = new Intl.DateTimeFormat('fr-CA', {
	weekday: 'long',
	year: 'numeric',
	month: 'long',
	day: 'numeric',
}).format(ELECTION_DATE)

const pad = (n: number) => n.toString().padStart(2, '0')

export function HomeCountdown({ serverNow }: HomeCountdownProps) {
	const now = useNow(serverNow)
	const delta = ELECTION_DATE.getTime() - now

	if (delta <= 0) return null

	const totalSeconds = Math.floor(delta / 1000)
	const days = Math.floor(totalSeconds / 86400)
	const hours = Math.floor((totalSeconds / 3600) % 24)
	const minutes = Math.floor((totalSeconds / 60) % 60)
	const seconds = totalSeconds % 60

	const mandateMs = ELECTION_DATE.getTime() - MANDATE_START_DATE.getTime()
	const elapsedMs = now - MANDATE_START_DATE.getTime()
	const progressPercent = Math.max(
		0,
		Math.min(100, Math.round((elapsedMs / mandateMs) * 100)),
	)

	const ariaLabel = `${ELECTION_LABEL} — dans ${days} jours ${hours} heures ${minutes} minutes ${seconds} secondes`

	return (
		<section
			aria-label={ariaLabel}
			className="from-primary to-accent animate-roll-reveal full-bleed relative overflow-hidden rounded-none bg-gradient-to-br p-5 shadow-xl md:rounded-3xl md:p-10"
		>
			{/* Halos décoratifs — profondeur sans image */}
			<div
				aria-hidden="true"
				className="bg-primary-foreground/10 pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full blur-3xl"
			/>
			<div
				aria-hidden="true"
				className="bg-accent/50 pointer-events-none absolute -bottom-24 -left-12 h-56 w-56 rounded-full blur-3xl"
			/>

			<div className="relative text-center">
				<Vote
					className="text-primary-foreground/80 mx-auto h-7 w-7"
					strokeWidth={1.75}
					aria-hidden="true"
				/>

				<p className="text-primary-foreground/70 mt-3 text-xs font-semibold tracking-[0.2em] uppercase">
					{ELECTION_LABEL}
				</p>

				<div
					aria-hidden="true"
					className="bg-primary-foreground/30 mx-auto mt-3 h-px w-12"
				/>

				<div className="mt-4 flex flex-col items-center">
					{days > 0 && (
						<>
							<span className="text-primary-foreground text-mega font-black leading-none">
								{days}
							</span>
							<span className="text-primary-foreground/60 mt-1.5 text-xs font-semibold tracking-widest uppercase">
								{days === 1 ? 'jour' : 'jours'}
							</span>
						</>
					)}
					<p
						className="text-primary-foreground/90 mt-3 text-lg font-semibold tabular-nums md:text-xl"
						aria-hidden="true"
					>
						<span>{pad(hours)}</span>
						<span className="opacity-60">h </span>
						<span>{pad(minutes)}</span>
						<span className="opacity-60">m </span>
						<span>{pad(seconds)}</span>
						<span className="opacity-60">s</span>
					</p>
				</div>

				<div className="mx-auto mt-4 max-w-xs">
					<div className="bg-primary-foreground/15 h-1 w-full overflow-hidden rounded-full">
						<div
							className="bg-primary-foreground/80 h-full rounded-full transition-[width] duration-700 ease-out"
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
					<p className="text-primary-foreground/60 mt-2 text-[11px] font-semibold tracking-widest uppercase">
						{progressPercent}% du mandat écoulé
					</p>
				</div>

				<p className="text-primary-foreground/70 mt-4 text-sm capitalize">
					{formattedElectionDate}
				</p>
			</div>
		</section>
	)
}
